import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  Plus, Edit, Trash2, Shield, Check, CheckSquare, Loader2, AlertCircle, LayoutGrid, List, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { orgApi } from '../utils/apiV2';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import EmptyState from './EmptyState';
import { useRoles } from '../hooks/useData';
import { PrimaryButton, SecondaryButton, SuccessButton, LayoutActionButton } from './Buttons';
import StandardModal from './StandardModal';
import MassActionToolbar from './MassActionToolbar';
import CardListViewToggle from './CardListViewToggle';
import ResourceCard from './ResourceCard';
import ModalTabSelector from './ModalTabSelector';
import DeleteConfirmModal from './DeleteConfirmModal';
import { getAllTabIds } from '../config/navigation';
import { INTEL_TABS } from '../utils/intelPerms';

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
];

const RolesTab = () => {
  const { roles, isLoading, isError, refresh } = useRoles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [viewMode, setViewMode] = useState("card");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    permissions: [],
    tabPermissions: {}
  });
  
  const [modalStep, setModalStep] = useState(1);

  const handleOpenDialog = (role = null) => {
    if (role) {
      // Get the latest version of the role from the roles array (in case it was updated)
      const latestRole = roles.find(r => r.id === role.id) || role;
      let tabPerms = latestRole.tabPermissions || {};

      // Owner / system roles should have access to every current tab. If new tabs
      // (e.g. assignments, giftCards) were added after this role was created and
      // weren't migrated yet, auto-seed them with full permissions so they're not
      // grayed out in the UI.
      const isOwnerRole = (latestRole.name || '').toLowerCase() === 'owner' || latestRole.isOwner === true;
      if (isOwnerRole) {
        const seeded = { ...tabPerms };
        getAllTabIds().forEach(({ id }) => {
          if (!seeded[id]) {
            seeded[id] = { read: true, create: true, update: true, delete: true };
          } else if (typeof seeded[id] === 'boolean') {
            // Normalize legacy boolean permissions
            seeded[id] = { read: true, create: true, update: true, delete: true };
          }
        });
        INTEL_TABS.forEach(({ perm }) => {
          if (!seeded[perm]) seeded[perm] = { read: true, create: true, update: true, delete: true };
        });
        ['data_export', 'contact_details', 'reconcile'].forEach((p) => {
          if (!seeded[p]) seeded[p] = { read: true, create: true, update: false, delete: false };
        });
        tabPerms = seeded;
      }

      setEditingRole(latestRole);
      setFormData({
        name: latestRole.name,
        description: latestRole.description || '',
        color: latestRole.color || '#3B82F6',
        permissions: latestRole.permissions || ['view_bookings'],
        tabPermissions: tabPerms
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        permissions: ['view_bookings'],
        tabPermissions: {}
      });
    }
    setDialogOpen(true);
    setModalStep(1);
  };

  const handleSaveRole = async () => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRole) {
        await orgApi.updateRole(editingRole.id, formData);
        toast.success(`Role "${formData.name}" updated successfully!`);
      } else {
        await orgApi.createRole(formData);
        toast.success(`Role "${formData.name}" created successfully!`);
      }
      
      window.dispatchEvent(new Event('setupDataChanged'));
      setDialogOpen(false);
      // Force refresh with revalidation
      await refresh();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    setIsDeleting(true);
    try {
      await orgApi.deleteRole(roleToDelete.id);
      toast.success(`Role "${roleToDelete.name}" deleted successfully!`);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      refresh();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    if (massActionMode) {
      setSelectedRoles([]);
    }
  };

  // Error state
  if (isError) {
    return (
      <TabPageLayout
        title="Roles & Positions"
        subtitle="Define team positions"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load roles</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => refresh()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Roles & Positions"
      subtitle="Define team positions"
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2">
          <LayoutActionButton icon={Plus} onClick={() => handleOpenDialog()} data-testid="roles-add-button">
            Add
          </LayoutActionButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedRoles.length}
            onSelectAll={() => setSelectedRoles(roles.map(r => r.id))}
            onDeselectAll={() => setSelectedRoles([])}
            onDelete={async () => {
              if (window.confirm(`Delete ${selectedRoles.length} role(s)? This cannot be undone.`)) {
                try {
                  await Promise.all(selectedRoles.map(id => orgApi.deleteRole(id)));
                  toast.success(`Deleted ${selectedRoles.length} role(s)`);
                  setSelectedRoles([]);
                  refresh();
                } catch (error) {
                  toast.error('Failed to delete some roles');
                }
              }
            }}
            itemName="role"
          />
        )}

        {/* Roles Display */}
        {roles.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No Roles Created Yet"
            description="Create your first role to start organizing your team"
            actionLabel="Create First Role"
            onAction={() => handleOpenDialog()}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => {
              const isSelected = selectedRoles.includes(role.id);
              return (
                <ResourceCard
                  key={role.id}
                  id={role.id}
                  name={role.name}
                  icon={Shield}
                  iconColor={role.color || '#3B82F6'}
                  description={undefined}
                  descriptionLines={[{
                    icon: <Lock className="w-3 h-3" />,
                    text: role.tabPermissions
                      ? Object.entries(role.tabPermissions).filter(([, v]) => v).map(([k]) => k.charAt(0).toUpperCase() + k.slice(1)).join(', ') || role.description || ''
                      : role.description || ''
                  }]}
                  leftBorderColors={[role.color || '#3B82F6']}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={(e) => {
                    if (e.target.checked) {
                      setSelectedRoles([...selectedRoles, role.id]);
                    } else {
                      setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                    }
                  }}
                  onEdit={() => handleOpenDialog(role)}
                  onDelete={() => openDeleteDialog(role)}
                />
              );
            })}
          </div>
        ) : (
          <div className="card-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50/60 border-b border-gray-100">
                  <tr>
                    {massActionMode && (
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedRoles.length === roles.length && roles.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRoles(roles.map(r => r.id));
                            } else {
                              setSelectedRoles([]);
                            }
                          }}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {roles.map((role) => {
                    const isSelected = selectedRoles.includes(role.id);
                    return (
                      <tr 
                        key={role.id} 
                        className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        {massActionMode && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRoles([...selectedRoles, role.id]);
                                } else {
                                  setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                                }
                              }}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${role.color}20`, border: `2px solid ${role.color}` }}
                            >
                              <Shield className="w-5 h-5" style={{ color: role.color }} />
                            </div>
                            <span className="font-medium text-gray-900">{role.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-500">
                            {role.description || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(role)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(role)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Role Modal */}
        <StandardModal
          isOpen={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setModalStep(1);
          }}
          title={editingRole ? 'Edit Role' : 'Create New Role'}
          size="md"
          dataTestId="role-create-modal"
          footer={
            <>
              {modalStep === 2 && (
                <SecondaryButton
                  onClick={() => setModalStep(1)}
                  className="flex-1"
                  disabled={isSaving}
                >
                  Back
                </SecondaryButton>
              )}
              {modalStep === 1 ? (
                <>
                  <SecondaryButton
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={() => setModalStep(2)}
                    className="flex-1"
                    disabled={!formData.name.trim()}
                  >
                    Next
                  </PrimaryButton>
                </>
              ) : (
                <SuccessButton
                  onClick={handleSaveRole}
                  className="flex-1"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingRole ? 'Update Role' : 'Create Role'}
                    </>
                  )}
                </SuccessButton>
              )}
            </>
          }
        >
          {/* Tab Selector (Only for Editing) */}
          {editingRole && (
            <div className="mb-5">
              <ModalTabSelector
                tabs={[
                  { value: 1, label: 'Role', icon: Shield },
                  { value: 2, label: 'Permissions', icon: Lock },
                ]}
                value={modalStep}
                onChange={setModalStep}
              />
            </div>
          )}
          
          {/* Step 1: Basic Info */}
          {modalStep === 1 && (
            <div className="space-y-5">
              {/* Live preview chip — mirrors the role card aesthetic */}
              <div className="flex items-center gap-3 rounded-2xl bg-gray-50/70 px-4 py-3.5">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{ backgroundColor: `${formData.color}14` }}
                >
                  <Shield className="w-5 h-5" style={{ color: formData.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-medium text-gray-900 tracking-tight truncate">
                    {formData.name.trim() || 'New role'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {formData.description?.trim() || 'Set a name, color & permissions'}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="name">Role Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  data-testid="role-name-input"
                  placeholder="e.g., Captain, Check-In Staff, Manager"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`mt-1.5 ${!formData.name.trim() && formData.name !== '' ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {!formData.name.trim() && formData.name !== '' && (
                  <p className="text-sm text-red-500 mt-1">Role name is required</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="Brief description of this role"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label>Color</Label>
                <div className="flex gap-2.5 flex-wrap mt-2 px-0.5 py-1.5">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110 ${
                        formData.color === color ? 'ring-2 ring-offset-2 ring-gray-900/70 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Select color ${color}`}
                    >
                      {formData.color === color && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Tab Permissions */}
          {modalStep === 2 && (
            <div className="space-y-3">
              {/* Quick Action Bar */}
              <div className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 bg-gray-50">
                <span className="text-xs font-medium text-gray-500 flex-shrink-0">Quick:</span>
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" onClick={() => {
                    const newPerms = {};
                    getAllTabIds().forEach(tab => { newPerms[tab.id] = { read: true, create: false, update: false, delete: false }; });
                    setFormData({ ...formData, tabPermissions: newPerms });
                  }} className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-md hover:bg-blue-100 transition-colors">
                    View Only
                  </button>
                  <button type="button" onClick={() => {
                    const newPerms = {};
                    getAllTabIds().forEach(tab => { newPerms[tab.id] = { read: true, create: true, update: true, delete: true }; });
                    setFormData({ ...formData, tabPermissions: newPerms });
                  }} className="px-2 py-1 bg-green-50 border border-green-200 text-green-700 text-xs rounded-md hover:bg-green-100 transition-colors">
                    Full Access
                  </button>
                  <button type="button" onClick={() => setFormData({ ...formData, tabPermissions: {} })}
                    className="px-2 py-1 bg-white border border-gray-200 text-gray-500 text-xs rounded-md hover:bg-gray-100 transition-colors">
                    Clear
                  </button>
                </div>
              </div>
              
              {/* Tab Permissions List — no inner scroll, let modal body scroll */}
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide pt-1">Operator Dashboard</p>
              <div className="space-y-1.5">
                {getAllTabIds().map(tab => {
                  const perms = formData.tabPermissions?.[tab.id] || {};
                  const isLegacy = typeof perms === 'boolean';
                  const readPerm = isLegacy ? perms : (perms.read || false);
                  const createPerm = isLegacy ? false : (perms.create || false);
                  const updatePerm = isLegacy ? false : (perms.update || false);
                  const deletePerm = isLegacy ? false : (perms.delete || false);
                  
                  return (
                    <div key={tab.id} className={`px-3.5 py-3 rounded-xl transition-all ${
                      readPerm ? 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)]' : 'bg-gray-50'
                    }`}>
                      {/* Mobile: 2-line layout / Desktop: single row */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        {/* Tab Name - Full width on mobile */}
                        <div className="flex items-center gap-2">
                          <span className={`text-[15px] font-medium ${readPerm ? 'text-gray-900' : 'text-gray-400'}`}>
                            {tab.label}
                          </span>
                        </div>
                        {/* Permission Checkboxes - Row below on mobile */}
                        <div className="flex items-center gap-4 sm:gap-5 flex-shrink-0">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={readPerm}
                              onChange={(e) => {
                                const newPerms = { ...formData.tabPermissions };
                                if (e.target.checked) {
                                  newPerms[tab.id] = { ...(newPerms[tab.id] || {}), read: true };
                                } else {
                                  newPerms[tab.id] = { read: false, create: false, update: false, delete: false };
                                }
                                setFormData({ ...formData, tabPermissions: newPerms });
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={`text-[13px] ${readPerm ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>View</span>
                          </label>
                          <label className={`flex items-center gap-1.5 ${readPerm ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                            <input
                              type="checkbox"
                              checked={createPerm}
                              disabled={!readPerm}
                              onChange={(e) => {
                                const newPerms = { ...formData.tabPermissions };
                                newPerms[tab.id] = { ...newPerms[tab.id], create: e.target.checked };
                                setFormData({ ...formData, tabPermissions: newPerms });
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-40"
                            />
                            <span className={`text-[13px] ${createPerm ? 'text-green-600 font-medium' : 'text-gray-500'}`}>Create</span>
                          </label>
                          <label className={`flex items-center gap-1.5 ${readPerm ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                            <input
                              type="checkbox"
                              checked={updatePerm}
                              disabled={!readPerm}
                              onChange={(e) => {
                                const newPerms = { ...formData.tabPermissions };
                                newPerms[tab.id] = { ...newPerms[tab.id], update: e.target.checked };
                                setFormData({ ...formData, tabPermissions: newPerms });
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 disabled:opacity-40"
                            />
                            <span className={`text-[13px] ${updatePerm ? 'text-yellow-600 font-medium' : 'text-gray-500'}`}>Edit</span>
                          </label>
                          <label className={`flex items-center gap-1.5 ${readPerm ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                            <input
                              type="checkbox"
                              checked={deletePerm}
                              disabled={!readPerm}
                              onChange={(e) => {
                                const newPerms = { ...formData.tabPermissions };
                                newPerms[tab.id] = { ...newPerms[tab.id], delete: e.target.checked };
                                setFormData({ ...formData, tabPermissions: newPerms });
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-40"
                            />
                            <span className={`text-[13px] ${deletePerm ? 'text-red-600 font-medium' : 'text-gray-500'}`}>Delete</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Intelligence Suite permissions */}
              <div className="pt-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Intelligence Suite (HELM)</p>
                <div className="space-y-1.5">
                  {INTEL_TABS.map((tab) => {
                    const perms = formData.tabPermissions?.[tab.perm] || {};
                    const viewOn = perms.read === true;
                    const manageOn = perms.create === true;
                    const isCampaigns = tab.perm === 'intel_campaigns';
                    return (
                      <div key={tab.perm} data-testid={`role-intel-${tab.navId}`} className={`px-3.5 py-3 rounded-xl transition-all ${viewOn ? 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)]' : 'bg-gray-50'}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="min-w-0">
                            <span className={`text-[15px] font-medium ${viewOn ? 'text-gray-900' : 'text-gray-400'}`}>{tab.label}</span>
                            <p className="text-[11px] text-gray-400 leading-snug">{tab.desc}</p>
                          </div>
                          <div className="flex items-center gap-4 sm:gap-5 flex-shrink-0">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                data-testid={`role-intel-${tab.navId}-view`}
                                checked={viewOn}
                                onChange={(e) => {
                                  const newPerms = { ...formData.tabPermissions };
                                  if (e.target.checked) newPerms[tab.perm] = { ...(newPerms[tab.perm] || {}), read: true };
                                  else delete newPerms[tab.perm];
                                  setFormData({ ...formData, tabPermissions: newPerms });
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className={`text-[13px] ${viewOn ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>View</span>
                            </label>
                            {isCampaigns && (
                              <label className={`flex items-center gap-1.5 ${viewOn ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                                <input
                                  type="checkbox"
                                  data-testid="role-intel-campaigns-manage"
                                  checked={manageOn}
                                  disabled={!viewOn}
                                  onChange={(e) => {
                                    const newPerms = { ...formData.tabPermissions };
                                    newPerms[tab.perm] = { ...(newPerms[tab.perm] || { read: true }), create: e.target.checked, update: e.target.checked, delete: e.target.checked };
                                    setFormData({ ...formData, tabPermissions: newPerms });
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-40"
                                />
                                <span className={`text-[13px] ${manageOn ? 'text-green-600 font-medium' : 'text-gray-500'}`}>Create &amp; launch</span>
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Intelligence tabs are hidden by default — grant only what this role needs. Audience contact details and exports are controlled separately below.</p>
              </div>

              {/* Special abilities (not tied to a tab) */}
              <div className="mt-3 p-3 border rounded-lg bg-amber-50/50 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Special Abilities</p>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <p className="text-[13px] font-medium text-gray-800">Reconcile payments</p>
                    <p className="text-xs text-gray-500">Charge a booking's saved card after the trip (gratuity, damage, fuel)</p>
                  </div>
                  <input
                    type="checkbox"
                    data-testid="role-reconcile-toggle"
                    checked={!!(formData.tabPermissions?.reconcile?.create)}
                    onChange={(e) => setFormData(prev => {
                      const tp = { ...prev.tabPermissions };
                      if (e.target.checked) tp.reconcile = { read: true, create: true, update: false, delete: false };
                      else delete tp.reconcile;
                      return { ...prev, tabPermissions: tp };
                    })}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer border-t border-amber-100 pt-3">
                  <div>
                    <p className="text-[13px] font-medium text-gray-800">Export data (CSV)</p>
                    <p className="text-xs text-gray-500">Download contact lists and report data. Off by default — the #1 data-protection control.</p>
                  </div>
                  <input
                    type="checkbox"
                    data-testid="role-data-export-toggle"
                    checked={!!(formData.tabPermissions?.data_export?.read)}
                    onChange={(e) => setFormData(prev => {
                      const tp = { ...prev.tabPermissions };
                      if (e.target.checked) tp.data_export = { read: true, create: true, update: false, delete: false };
                      else delete tp.data_export;
                      return { ...prev, tabPermissions: tp };
                    })}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer border-t border-amber-100 pt-3">
                  <div>
                    <p className="text-[13px] font-medium text-gray-800">View customer contact details</p>
                    <p className="text-xs text-gray-500">See raw emails and phone numbers in Audiences, Customer Insights and campaign activity. Otherwise they're masked (j•••@gmail.com) — segment sizes still visible.</p>
                  </div>
                  <input
                    type="checkbox"
                    data-testid="role-contact-details-toggle"
                    checked={!!(formData.tabPermissions?.contact_details?.read)}
                    onChange={(e) => setFormData(prev => {
                      const tp = { ...prev.tabPermissions };
                      if (e.target.checked) tp.contact_details = { read: true, create: false, update: false, delete: false };
                      else delete tp.contact_details;
                      return { ...prev, tabPermissions: tp };
                    })}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                </label>
              </div>
              
              {/* Compact Summary */}
              <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                <span>
                  <strong className="text-gray-700">{Object.keys(formData.tabPermissions || {}).filter(k => {
                    const p = formData.tabPermissions[k];
                    return typeof p === 'boolean' ? p : p?.read;
                  }).length}</strong> accessible
                </span>
                <span>
                  <strong className="text-green-600">{Object.keys(formData.tabPermissions || {}).filter(k => {
                    const p = formData.tabPermissions[k];
                    return typeof p !== 'boolean' && (p?.create || p?.update || p?.delete);
                  }).length}</strong> with write
                </span>
              </div>
            </div>
          )}
        </StandardModal>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteRole}
          title="Delete Role?"
          description={`Are you sure you want to delete the role "${roleToDelete?.name}"?`}
          isDeleting={isDeleting}
          confirmLabel="Delete Role"
        >
          <p className="font-medium mb-1">Heads up!</p>
          <p>Any team members who have this role will need to be reassigned to a different role.</p>
        </DeleteConfirmModal>
      </div>
    </TabPageLayout>
  );
};

export default RolesTab;
